import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

interface VoiceTextDisplayProps {
  className?: string;
  isVoiceMode: boolean;
}

export interface VoiceTextDisplayRef {
  getTextDisplayElement: () => HTMLElement | null;
  getMathExpressionsElement: () => HTMLElement | null;
  getTypingIndicatorElement: () => HTMLElement | null;
  addUploadedImage: (image: File, message?: string) => void;
}

const VoiceTextDisplay = forwardRef<VoiceTextDisplayRef, VoiceTextDisplayProps>(
  ({ className = '', isVoiceMode }, ref) => {
    const textDisplayRef = useRef<HTMLDivElement>(null);
    const mathExpressionsRef = useRef<HTMLDivElement>(null);
    const typingIndicatorRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      getTextDisplayElement: () => textDisplayRef.current,
      getMathExpressionsElement: () => mathExpressionsRef.current,
      getTypingIndicatorElement: () => typingIndicatorRef.current,
      addUploadedImage: (image: File, message?: string) => {
        if (!textDisplayRef.current) return;

        const imageContainer = document.createElement('div');
        imageContainer.className = 'uploaded-image-container';
        imageContainer.style.cssText = `
          margin-bottom: 20px;
          padding: 16px;
          background: #e0f2fe;
          border-radius: 8px;
          border-left: 4px solid #0ea5e9;
        `;

        // Add timestamp and source indicator
        const headerDiv = document.createElement('div');
        headerDiv.style.cssText = `
          font-size: 12px;
          color: #0369a1;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        `;
        headerDiv.innerHTML = `<span>📷</span> Image uploaded - ${new Date().toLocaleTimeString()}`;
        imageContainer.appendChild(headerDiv);

        // Add message if provided
        if (message) {
          const messageDiv = document.createElement('div');
          messageDiv.style.cssText = `
            color: #0c4a6e;
            margin-bottom: 12px;
            font-style: italic;
          `;
          messageDiv.textContent = `"${message}"`;
          imageContainer.appendChild(messageDiv);
        }

        // Add image preview
        const img = document.createElement('img');
        img.src = URL.createObjectURL(image);
        img.alt = 'Uploaded image';
        img.style.cssText = `
          max-width: 100%;
          max-height: 300px;
          border-radius: 6px;
          border: 1px solid #bae6fd;
        `;
        
        // Clean up URL when image loads
        img.onload = () => {
          URL.revokeObjectURL(img.src);
        };
        
        imageContainer.appendChild(img);

        // Insert at the top (most recent first)
        if (textDisplayRef.current.firstChild) {
          textDisplayRef.current.insertBefore(imageContainer, textDisplayRef.current.firstChild);
        } else {
          textDisplayRef.current.appendChild(imageContainer);
        }

        // Auto-scroll to top to show latest content
        if (textDisplayRef.current.parentElement) {
          textDisplayRef.current.parentElement.scrollTop = 0;
        }
      },
    }));

    useEffect(() => {
      // Initialize display based on mode
      if (textDisplayRef.current) {
        if (isVoiceMode) {
          textDisplayRef.current.innerHTML = `
            <div class="welcome-message text-center" style="margin-top: 150px; color: #6b7280;">
              <div style="font-size: 48px; margin-bottom: 24px;">🎙️</div>
              <div style="font-size: 18px; font-weight: 500; margin-bottom: 12px;">Voice Session Active</div>
              <div style="font-size: 14px;">The AI tutor will present content here while speaking</div>
            </div>
          `;
        } else {
          textDisplayRef.current.innerHTML = `
            <div class="welcome-message text-gray-500 dark:text-gray-400 text-center italic mt-12">
              Click "Start Voice Session" to begin voice interaction with the AI tutor.
            </div>
          `;
        }
      }
    }, [isVoiceMode]);

    if (!isVoiceMode) {
      return null;
    }

    return (
      <div className={`voice-presentation-display ${className}`} style={{ position: 'relative', height: '100%' }}>
        {/* Main presentation screen - like a shared screen */}
        <div className="presentation-screen flex-1" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '500px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '2px solid #e2e8f0',
          borderRadius: '12px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Scrollable content area */}
          <div
            ref={textDisplayRef}
            className="presentation-content"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            <div className="welcome-message text-center" style={{ color: '#6b7280', marginTop: '100px' }}>
              <div style={{ fontSize: '48px', marginBottom: '24px' }}>🎙️</div>
              <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '12px' }}>Voice Session Active</div>
              <div style={{ fontSize: '14px' }}>The AI tutor will present content here while speaking</div>
            </div>
          </div>
        </div>

        {/* Audio indicator - bottom left */}
        <div 
          className="audio-indicator fixed"
          style={{
            bottom: '20px',
            left: '20px',
            background: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '24px',
            padding: '12px 16px',
            display: 'none', // Hidden by default, shown during speech
            alignItems: 'center',
            gap: '12px',
            color: 'white',
            fontSize: '14px',
            zIndex: 1000
          }}
        >
          <div className="audio-waves" style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <div className="wave" style={{
              width: '3px',
              height: '12px',
              background: '#22c55e',
              borderRadius: '2px',
              animation: 'audioWave 1.2s ease-in-out infinite',
              animationDelay: '0s'
            }}></div>
            <div className="wave" style={{
              width: '3px',
              height: '16px',
              background: '#22c55e',
              borderRadius: '2px',
              animation: 'audioWave 1.2s ease-in-out infinite',
              animationDelay: '0.1s'
            }}></div>
            <div className="wave" style={{
              width: '3px',
              height: '20px',
              background: '#22c55e',
              borderRadius: '2px',
              animation: 'audioWave 1.2s ease-in-out infinite',
              animationDelay: '0.2s'
            }}></div>
            <div className="wave" style={{
              width: '3px',
              height: '16px',
              background: '#22c55e',
              borderRadius: '2px',
              animation: 'audioWave 1.2s ease-in-out infinite',
              animationDelay: '0.3s'
            }}></div>
          </div>
          <span>AI Tutor</span>
        </div>

        {/* Thinking indicator */}
        <div
          ref={typingIndicatorRef}
          className="thinking-indicator hidden"
          style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '20px',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#3b82f6',
            fontSize: '14px'
          }}
        >
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <span>AI Tutor is thinking...</span>
        </div>

        {/* Hidden math expressions ref for compatibility */}
        <div ref={mathExpressionsRef} style={{ display: 'none' }}></div>
      </div>
    );
  }
);

VoiceTextDisplay.displayName = 'VoiceTextDisplay';

export default VoiceTextDisplay;
